import type { Extension, QuestionnaireResponseItem } from "@medplum/fhirtypes";
import {
  FLOW_DEFINITION_KIND,
  getTRPCClient,
  QUESTIONNAIRE_ANSWER_OCCURRENCE_URL,
  QUESTIONNAIRE_INSTANCE_COMPLETED_AT_URL,
  QUESTIONNAIRE_INSTANCE_ENTITY_ID_SYSTEM,
  QUESTIONNAIRE_INSTANCE_PROFILE,
  QUESTIONNAIRE_INSTANCE_UNDER_AMENDMENT_URL,
} from "@repo/api";
import {
  type AssignQuestionnaireInput,
  assignQuestionnaireInputSchema,
  type ClearQuestionnaireAnswerInput,
  type CompleteQuestionnaireInput,
  clearQuestionnaireAnswerInputSchema,
  completeQuestionnaireInputSchema,
  patientIdSchema,
  type QuestionnaireInstanceIdInput,
  questionnaireInstanceIdInputSchema,
  questionnaireInstanceIdSchema,
  type RecordQuestionnaireAnswerInput,
  type ReopenQuestionnaireInput,
  recordQuestionnaireAnswerInputSchema,
  reopenQuestionnaireInputSchema,
  type StartQuestionnaireFlowInput,
  startQuestionnaireFlowInputSchema,
} from "@repo/api/schemas";
import {
  asMemberId,
  disabledMemberIds,
  type EnablementItem,
  type EnablementSection,
  type PatientFactSource,
  type PatientId,
  type QuestionnaireInstanceId,
} from "@repo/domain";
import { extensionUrl } from "@repo/fhir-systems";
import { FLOW_INSTANCE_ENTITY_ID_SYSTEM } from "@repo/fhir-systems/identifiers";
import { and, eq, useLiveQuery } from "@tanstack/react-db";
import { defineWrite } from "../actions/define-write";
import { useCollection } from "../core/hooks";
import { getDataStoreClient } from "../core/provider";
import { DomainResultError } from "../core/write-errors";
import type { EnablementCondition, QuestionnaireSection } from "../fhir/questionnaire-definition-storage";
import { readQuestionnaireDefinitionRow } from "../fhir/questionnaire-definition-storage";
import { questionnaireInstanceAnswers, questionnaireInstanceAssignedAt } from "../fhir/questionnaire-instance-storage";
import type { QuestionnaireInstanceRow, QuestionnaireRow } from "../fhir/types";
import { recordOptimisticQuestionnaireAnswer } from "../lib/questionnaire-instance-optimistic";
import { projectQuestionnaireInstanceDto } from "../lib/questionnaire-instance-projection";
import { optimisticPatientReference } from "./_patient-reference";
import { projectFlowDefinition } from "./flow-definition";

const INSTANCE_COLLECTION = "questionnaireResponse";
const DEFINITION_COLLECTION = "questionnaire";

export interface QuestionnaireInstanceDto {
  instanceId: QuestionnaireInstanceId;
  patientId: PatientId;
  definition: {
    url: string;
    version: string;
    name: string;
  };
  status: "inProgress" | "completed" | "underAmendment" | "amended" | "stopped" | "enteredInError";
  /**
   * Whether anything has been answered yet.
   *
   * FHIR's required `QuestionnaireResponse.status` has no "not started" — a
   * persisted response claims `in-progress` from the moment it exists — so
   * `status` alone cannot tell an assessment nobody has opened from one being
   * worked. This is that distinction; `status` still carries the lifecycle, and
   * charting surfaces gate on it rather than on this.
   */
  started: boolean;
  /** When the instance was created, read off its minted identity. Absent for a given id. */
  createdAt?: string;
  assignedAt: string;
  completedAt?: string;
  sections: readonly QuestionnaireInstanceSectionDto[];
  scoreDefinitions?: readonly {
    scoreId: string;
    label: string;
    minimum: number;
    maximum: number;
    itemIds: readonly string[];
    bands?: readonly { label: string; from: number; to?: number }[];
  }[];
}

/**
 * When a section or question is asked, from the pinned revision. Carried so a
 * renderer can evaluate enablement against the answers IT holds — including
 * edits not yet saved — with the domain's `disabledMemberIds`, the same
 * evaluator completion uses to prune and excuse.
 */
interface QuestionnaireInstanceEnablementDto {
  conditions?: readonly EnablementCondition[];
  /** Present only when it is "any"; absent reads as "all". */
  conditionBehavior?: "any" | "all";
}

interface QuestionnaireInstanceSectionDto extends QuestionnaireInstanceEnablementDto {
  sectionId: string;
  label: string;
  /** Asked as one screen rather than one question at a time. */
  showTogether?: boolean;
  items: readonly QuestionnaireInstanceItemDto[];
}

type QuestionnaireInstanceItemDto =
  | { kind: "Display"; itemId: string; text: string }
  | {
      kind: "RepeatingGroup";
      itemId: string;
      label: string;
      minOccurrences: number;
      items: readonly QuestionnaireInstanceItemDto[];
    }
  | (QuestionnaireInstanceEnablementDto & {
      kind: "Question";
      help?: string;
      itemId: string;
      label: string;
      required: boolean;
      answerType: "Quantity" | "Integer" | "Choice" | "Boolean" | "Text" | "Date" | "Attachment" | "Url";
      /** On a Text question: how it is entered. Absent is a paragraph. */
      textFormat?: "SingleLine" | "Email" | "Phone";
      /** On an Integer question: the declared range and increment. */
      min?: number;
      max?: number;
      step?: number;
      choiceCardinality?: "Single" | "Multiple";
      allowFreeText?: boolean;
      options: readonly { optionId: string; label: string; detail?: string; points?: number }[];
      /** Present when the question asks Yes/No first: the option a No answers with. */
      noOptionId?: string;
      answers: readonly QuestionnaireInstanceAnswerDto[];
    });

interface QuestionnaireInstanceAnswerDto {
  occurrence?: number;
  value:
    | { kind: "Quantity"; value: number; unit?: { code: string; display: string } }
    | { kind: "Integer"; value: number }
    | { kind: "Choice"; optionId?: string; freeText?: string; display: string }
    | { kind: "Boolean"; value: boolean }
    | { kind: "Text"; value: string }
    | { kind: "Date"; value: string }
    | { kind: "Attachment"; url: string; contentType?: string; title?: string }
    | { kind: "Url"; value: string };
}

export interface QuestionnaireInstanceFilters {
  patientId?: PatientId;
  definitionUrl?: string;
}

export function useQuestionnaireInstances(filters: QuestionnaireInstanceFilters = {}): QuestionnaireInstanceDto[] {
  const instanceCollection = useCollection(INSTANCE_COLLECTION);
  const definitionCollection = useCollection(DEFINITION_COLLECTION);
  const result = useLiveQuery({
    queryKey: [
      instanceCollection.id,
      definitionCollection.id,
      "questionnaire-instances",
      filters.patientId,
      filters.definitionUrl,
    ],
    query: (query) => {
      const instances = query
        .from({ instance: instanceCollection })
        .where(({ instance }) => eq(instance.questionnaireInstance, true));
      const forPatient = filters.patientId
        ? instances.where(({ instance }) => eq(instance.patientId, filters.patientId))
        : instances;
      const matching = filters.definitionUrl
        ? forPatient.where(({ instance }) => eq(instance.definitionUrl, filters.definitionUrl))
        : forPatient;
      return (
        matching
          .innerJoin({ definition: definitionCollection }, ({ instance, definition }) =>
            eq(instance.definitionRef, definition.questionnaireDefinitionRef),
          )
          .where(({ definition }) => eq(definition.questionnaireDefinition, true))
          .orderBy(({ instance }) => instance.instanceAssignedAt, "desc")
          .orderBy(({ instance }) => instance.id, "asc")
          // TanStack DB cannot declaratively decode nested FHIR items/extensions;
          // keep that unsupported final transformation inside this live pipeline.
          .fn.select(({ instance, definition }) => ({
            ...projectQuestionnaireInstanceDto(instance, definition),
            instanceId: questionnaireInstanceIdSchema.parse(instance.domainId),
            patientId: patientIdSchema.parse(instance.patientId),
          }))
      );
    },
  });
  return result.data ?? [];
}

export function useQuestionnaireInstance(
  id: QuestionnaireInstanceId | undefined,
): QuestionnaireInstanceDto | undefined {
  const instanceCollection = useCollection(INSTANCE_COLLECTION);
  const definitionCollection = useCollection(DEFINITION_COLLECTION);
  const result = useLiveQuery({
    queryKey: [instanceCollection.id, definitionCollection.id, "questionnaire-instance", id],
    query: (query) => {
      if (!id) return undefined;
      return (
        query
          .from({ instance: instanceCollection })
          .where(({ instance }) => and(eq(instance.questionnaireInstance, true), eq(instance.domainId, id)))
          .innerJoin({ definition: definitionCollection }, ({ instance, definition }) =>
            eq(instance.definitionRef, definition.questionnaireDefinitionRef),
          )
          .where(({ definition }) => eq(definition.questionnaireDefinition, true))
          // TanStack DB cannot declaratively decode nested FHIR items/extensions;
          // keep that unsupported final transformation inside this live pipeline.
          .fn.select(({ instance, definition }) => ({
            ...projectQuestionnaireInstanceDto(instance, definition),
            instanceId: questionnaireInstanceIdSchema.parse(instance.domainId),
            patientId: patientIdSchema.parse(instance.patientId),
          }))
          .findOne()
      );
    },
  });
  return result.data;
}

const instances = () => getDataStoreClient().getCollection(INSTANCE_COLLECTION);
const definitions = () => getDataStoreClient().getCollection(DEFINITION_COLLECTION);

function insertAssignedQuestionnaire(params: AssignQuestionnaireInput) {
  const input = assignQuestionnaireInputSchema.parse(params);
  const definition = currentDefinition(input.definitionUrl);
  if (definition.status !== "active") throw new Error(`Questionnaire definition '${input.definitionUrl}' is inactive`);
  const definitionRef = definition.questionnaireDefinitionRef;
  if (!definitionRef) throw new Error(`Questionnaire definition '${input.definitionUrl}' has no revision pin`);
  const assignedAt = new Date().toISOString();
  const optimisticInstanceId = input.id;
  instances().insert({
    resourceType: "QuestionnaireResponse",
    meta: { profile: [QUESTIONNAIRE_INSTANCE_PROFILE] },
    identifier: { system: QUESTIONNAIRE_INSTANCE_ENTITY_ID_SYSTEM, value: optimisticInstanceId },
    status: "in-progress",
    subject: optimisticPatientReference(input.patientId),
    questionnaire: definitionRef,
    authored: assignedAt,
    questionnaireInstance: true,
    domainId: optimisticInstanceId,
    patientId: input.patientId,
    definitionRef,
    definitionUrl: definition.url,
    definitionVersion: definition.version,
    instanceAssignedAt: assignedAt,
    instanceStatus: "inProgress",
    instanceOpen: true,
  });
}

export const assignQuestionnaire = defineWrite<AssignQuestionnaireInput>({
  optimistic: insertAssignedQuestionnaire,
  persist: async (input, options) => {
    await getTRPCClient().questionnaire.assign.mutate(input, options);
  },
});

const startFlow = defineWrite<StartQuestionnaireFlowInput>({
  optimistic: (params) => {
    const input = startQuestionnaireFlowInputSchema.parse(params);
    const client = getDataStoreClient();
    const candidates = [...client.getCollection("planDefinition").values()]
      .filter((row) => row.url === input.flowUrl && row.catalogModelKind === FLOW_DEFINITION_KIND)
      .sort((a, b) => Number(b.version) - Number(a.version));
    const row = candidates[0];
    if (!row || row.status !== "active")
      throw new DomainResultError("This flow is not available. Refresh and try again.");
    const flow = projectFlowDefinition(row);
    const targets = new Set(flow.edges.map((edge) => edge.to));
    const entry = flow.nodes.find((node) => !targets.has(node.id));
    if (!entry) throw new DomainResultError("This flow has no entry form.");
    const pins = flow.nodes.map((node) => ({ node, definition: currentDefinition(node.questionnaire) }));
    insertAssignedQuestionnaire({
      id: input.instanceId,
      patientId: input.patientId,
      definitionUrl: entry.questionnaire,
    });
    instances().update(input.instanceId, (draft) => {
      draft.extension = [
        {
          url: extensionUrl("questionnaire-instance-flow-step"),
          extension: [
            {
              url: "flowInstance",
              valueReference: { identifier: { system: FLOW_INSTANCE_ENTITY_ID_SYSTEM, value: input.id } },
            },
            { url: "node", valueString: entry.id },
          ],
        },
      ];
    });
    client.getCollection("requestGroup").insert({
      resourceType: "RequestGroup",
      meta: { profile: [extensionUrl("flow-instance")] },
      identifier: [{ system: FLOW_INSTANCE_ENTITY_ID_SYSTEM, value: input.id }],
      instantiatesCanonical: [`${flow.url}|${flow.version}`],
      status: "active",
      intent: "plan",
      subject: optimisticPatientReference(input.patientId),
      authoredOn: new Date().toISOString(),
      domainId: input.id,
      isRedesignedOrderSet: false,
      extension: pins.map(({ node, definition }) => ({
        url: extensionUrl("flow-instance-pin"),
        extension: [
          { url: "node", valueString: node.id },
          { url: "questionnaire", valueCanonical: definition.questionnaireDefinitionRef },
        ],
      })),
      action: [
        {
          id: entry.id,
          resource: { identifier: { system: QUESTIONNAIRE_INSTANCE_ENTITY_ID_SYSTEM, value: input.instanceId } },
        },
      ],
    });
  },
  persist: async (input, options) => {
    await getTRPCClient().questionnaire.startFlow.mutate(input, options);
  },
});

export async function startQuestionnaireFlow(input: StartQuestionnaireFlowInput): Promise<void> {
  await startFlow(input).isPersisted.promise;
}

export const recordQuestionnaireAnswer = defineWrite<RecordQuestionnaireAnswerInput>({
  optimistic: (params) => {
    const input = recordQuestionnaireAnswerInputSchema.parse(params);
    const row = instanceRow(input.instanceId);
    const definition = pinnedDefinition(row.definitionRef);
    const question = findQuestion(definition.sections, input.questionId);
    if (!question) throw new Error(`Question '${input.questionId}' is not in the pinned questionnaire definition`);
    instances().update(instanceResourceId(input.instanceId), (draft) => {
      draft.item = recordOptimisticQuestionnaireAnswer(draft.item, question, input);
    });
  },
  persist: async (input, options) => {
    await getTRPCClient().questionnaire.recordAnswer.mutate(input, options);
  },
});

export const clearQuestionnaireAnswer = defineWrite<ClearQuestionnaireAnswerInput>({
  optimistic: (params) => {
    const input = clearQuestionnaireAnswerInputSchema.parse(params);
    instanceRow(input.instanceId);
    instances().update(instanceResourceId(input.instanceId), (draft) => {
      const remaining = (draft.item ?? []).filter(
        (item) => item.linkId !== input.questionId || occurrenceOf(item) !== input.occurrence,
      );
      if (remaining.length) draft.item = remaining;
      else delete draft.item;
    });
  },
  persist: async (input, options) => {
    await getTRPCClient().questionnaire.clearAnswer.mutate(input, options);
  },
});

export const completeQuestionnaire = defineWrite<CompleteQuestionnaireInput>({
  optimistic: (params) => {
    const input = completeQuestionnaireInputSchema.parse(params);
    const row = instanceRow(input.instanceId);
    const definition = pinnedDefinition(row.definitionRef);
    const sections = enablementSections(definition.sections);
    const disabled: ReadonlySet<string> = disabledMemberIds(
      sections,
      questionnaireInstanceAnswers(row).map((answer) => ({
        questionId: answer.questionId,
        occurrence: answer.occurrence,
        value:
          answer.value.kind === "Choice"
            ? (answer.value.optionId ?? answer.value.freeText)
            : answer.value.kind === "Attachment"
              ? answer.value.url
              : answer.value.value,
      })),
      patientFactsFor(sections, row),
    );
    const amended = row.instanceStatus === "underAmendment";
    setStatus(
      input.instanceId,
      amended ? "amended" : "completed",
      amended ? "amended" : "completed",
      false,
      new Date().toISOString(),
    );
    instances().update(input.instanceId, (draft) => {
      draft.item = draft.item?.filter((item) => !disabled.has(item.linkId));
      draft.extension = draft.extension?.filter((ext) => ext.url !== extensionUrl("questionnaire-instance-amendment"));
    });
  },
  persist: async (input, options) => {
    await getTRPCClient().questionnaire.complete.mutate(input, options);
  },
});

export const reopenQuestionnaire = defineWrite<ReopenQuestionnaireInput>({
  optimistic: (params) => {
    const input = reopenQuestionnaireInputSchema.parse(params);
    const row = instanceRow(input.instanceId);
    instances().update(instanceResourceId(input.instanceId), (draft) => {
      draft.status = "in-progress";
      draft.instanceStatus = "underAmendment";
      draft.instanceOpen = true;
      draft.extension = [
        ...(withUnderAmendment(row.extension, true) ?? []).filter(
          (ext) => ext.url !== extensionUrl("questionnaire-instance-amendment"),
        ),
        {
          url: extensionUrl("questionnaire-instance-amendment"),
          valueString: JSON.stringify({ baseline: questionnaireInstanceAnswers(row), reason: input.reason }),
        },
      ];
    });
  },
  persist: async (input, options) => {
    await getTRPCClient().questionnaire.reopen.mutate(input, options);
  },
});
export const stopQuestionnaire = terminalAction("stop");
export const retractQuestionnaire = terminalAction("retract");

function terminalAction(intent: "stop" | "retract") {
  return defineWrite<QuestionnaireInstanceIdInput>({
    optimistic: (params) => {
      const input = questionnaireInstanceIdInputSchema.parse(params);
      setStatus(
        input.instanceId,
        intent === "stop" ? "stopped" : "entered-in-error",
        intent === "stop" ? "stopped" : "enteredInError",
        false,
      );
    },
    persist: async (input, options) => {
      await getTRPCClient().questionnaire[intent].mutate(input, options);
    },
  });
}

function instanceRow(id: QuestionnaireInstanceId): QuestionnaireInstanceRow {
  const row = instances().get(id) as QuestionnaireInstanceRow | undefined;
  if (!row?.questionnaireInstance) throw new Error(`Questionnaire instance '${id}' is not available`);
  return row;
}

/** Rows are keyed by domain identity; this only asserts the instance is loaded. */
function instanceResourceId(id: QuestionnaireInstanceId): string {
  instanceRow(id);
  return id;
}

function currentDefinition(url: string) {
  let current: QuestionnaireRow | undefined;
  for (const row of definitions().state.values()) {
    if (!row.questionnaireDefinition || row.url !== url) continue;
    if (!current || row.catalogVersion > current.catalogVersion) current = row;
  }
  if (!current) throw new Error(`Questionnaire definition '${url}' is not available`);
  return current;
}

function pinnedDefinition(ref: string | undefined) {
  if (!ref) throw new Error("Questionnaire instance has no pinned definition");
  const [url, version] = ref.split("|");
  const row = [...definitions().state.values()].find(
    (candidate) => candidate.questionnaireDefinition && candidate.url === url && candidate.version === version,
  );
  if (!row) throw new Error(`Pinned questionnaire definition '${ref}' is not available`);
  return readQuestionnaireDefinitionRow(row);
}

function findQuestion(sections: readonly QuestionnaireSection[], questionId: string) {
  for (const section of sections) {
    for (const item of section.items) {
      if (item.kind === "Question" && String(item.id) === questionId) return item;
      if (item.kind === "RepeatingGroup") {
        const child = item.items.find(
          (candidate) => candidate.kind === "Question" && String(candidate.id) === questionId,
        );
        if (child?.kind === "Question") return child;
      }
    }
  }
  return undefined;
}

function occurrenceOf(item: QuestionnaireResponseItem): number | undefined {
  return item.extension?.find((extension) => extension.url === QUESTIONNAIRE_ANSWER_OCCURRENCE_URL)?.valueInteger;
}

function setStatus(
  id: QuestionnaireInstanceId,
  fhirStatus: "amended" | "completed" | "stopped" | "entered-in-error",
  status: "amended" | "completed" | "stopped" | "enteredInError",
  open: boolean,
  completedAt?: string,
): void {
  const row = instanceRow(id);
  instances().update(instanceResourceId(id), (draft) => {
    draft.status = fhirStatus;
    draft.instanceStatus = status;
    draft.instanceOpen = open;
    draft.extension = withUnderAmendment(row.extension, false);
    if (completedAt) {
      draft.extension = [
        ...(draft.extension ?? []).filter((extension) => extension.url !== QUESTIONNAIRE_INSTANCE_COMPLETED_AT_URL),
        { url: QUESTIONNAIRE_INSTANCE_COMPLETED_AT_URL, valueDateTime: completedAt },
      ];
    }
  });
}

function withUnderAmendment(extensions: Extension[] | undefined, enabled: boolean): Extension[] | undefined {
  const remaining = (extensions ?? []).filter(
    (extension) => extension.url !== QUESTIONNAIRE_INSTANCE_UNDER_AMENDMENT_URL,
  );
  const next = enabled
    ? [...remaining, { url: QUESTIONNAIRE_INSTANCE_UNDER_AMENDMENT_URL, valueBoolean: true }]
    : remaining;
  return next.length ? next : undefined;
}

/**
 * Only a form with an age condition needs the patient's birth date and the
 * assignment date, so only such a form reads them; any other completes without
 * the patient loaded. The evaluator reads the facts for a fact condition only.
 */
function patientFactsFor(sections: readonly EnablementSection[], row: QuestionnaireInstanceRow): PatientFactSource {
  const readsFacts = sections.some((section) =>
    [section, ...section.items.flatMap((item) => (item.kind === "RepeatingGroup" ? item.items : [item]))].some(
      (holder) => "conditions" in holder && holder.conditions?.some((condition) => "fact" in condition),
    ),
  );
  if (!readsFacts) return { birthDate: "", assignedAt: "" };
  const patient = [...getDataStoreClient().getCollection("patient").values()].find(
    (candidate) => candidate.domainId === row.patientId,
  );
  if (!patient?.birthDate) throw new Error("Patient birth date is required to evaluate questionnaire conditions");
  return { birthDate: patient.birthDate, assignedAt: questionnaireInstanceAssignedAt(row) };
}

function enablementSections(sections: readonly QuestionnaireSection[]): EnablementSection[] {
  const conditions = (holder: { conditions?: readonly EnablementCondition[]; conditionBehavior?: "any" | "all" }) => ({
    conditionBehavior: holder.conditionBehavior,
    conditions: holder.conditions?.map((condition) =>
      "fact" in condition
        ? condition
        : {
            ...condition,
            question: asMemberId(condition.question),
            value:
              condition.value.kind === "Choice"
                ? { ...condition.value, optionId: asMemberId(condition.value.optionId) }
                : condition.value,
          },
    ),
  });
  const item = (value: QuestionnaireSection["items"][number]): EnablementItem => {
    if (value.kind === "Display") return { kind: "Display" };
    if (value.kind === "RepeatingGroup")
      return {
        kind: "RepeatingGroup",
        items: value.items.map((child) =>
          child.kind === "Display"
            ? { kind: "Display" }
            : { kind: "Question", id: asMemberId(child.id), ...conditions(child) },
        ),
      };
    return { kind: "Question", id: asMemberId(value.id), ...conditions(value) };
  };
  return sections.map((section) => ({
    id: asMemberId(section.id),
    ...conditions(section),
    items: section.items.map(item),
  }));
}
